import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Minus, Plus, Trash2, X } from "lucide-react";
import { useCart, DELIVERY_FEE } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { formatPrice, formatDate } from "@/lib/utils";
import { buildShopifyCheckout } from "@/utils/shopifyCheckout";
import { track, appendAttribution } from "@/utils/metaPixel";

export const CartDrawer = () => {
    const { items, isOpen, closeCart, updateQty, removeItem, subtotal, deliveryFee, freeDelivery } = useCart();
    const { lang, t } = useLang();
    const [checkingOut, setCheckingOut] = useState(false);

    const checkoutShopify = async () => {
        setCheckingOut(true);
        try {
            const payload = buildShopifyCheckout(items);
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shopify/checkout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok || !data.url) throw new Error("checkout failed");
            /* InitiateCheckout juste avant la redirection vers la caisse Shopify. */
            track("InitiateCheckout", {
                content_ids: payload.items.map((i) => i.variantId),
                num_items: items.reduce((n, it) => n + (it.qty || 1), 0),
                value: Number((subtotal + (freeDelivery ? 0 : deliveryFee)).toFixed(2)),
                currency: "EUR",
            });
            window.location.href = appendAttribution(data.url);
            return;
        } catch {
            /* Shopify indisponible → repli démo */
        }
        try {
            const { toast } = await import("sonner");
            toast(t("cart.demoCheckout"), { description: t("cart.demoCheckoutDesc") });
        } finally {
            setCheckingOut(false);
        }
    };

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    const totalQty = items.reduce((s, it) => s + it.qty, 0);
    const total = subtotal + (items.length ? deliveryFee : 0);

    const optionsSummary = (it) => {
        const lines = [];
        if (it.options.properties?.length && it.productId === "romantic-pack-supreme") {
            it.options.properties.forEach(([k, v]) => lines.push(`${k}: ${v}`));
            return lines;
        }
        if (it.options.teddyColorLabel)
            lines.push(`${t("cart.color")}: ${it.options.teddyColorLabel}`);
        if (it.options.shirt)
            lines.push(
                `${t("cart.shirtOsito")}${it.options.shirtColorLabel ? ` · ${it.options.shirtColorLabel}` : ""}`
            );
        if (it.options.roseCount)
            lines.push(`${it.options.roseCount} ${t("cart.roses")}`);
        if (it.options.shirtText)
            lines.push(`${t("cart.shirt")}: “${it.options.shirtText}”`);
        if (it.options.packExtra) lines.push(t("cart.packExtra"));
        if (it.options.cardStyleLabel)
            lines.push(`${t("cart.cardStyle")}: ${it.options.cardStyleLabel}`);
        if (it.options.message)
            lines.push(`${t("cart.card")}: “${it.options.message}”`);
        if (it.options.deliveryDate)
            lines.push(
                `${t("cart.deliveryOn")}: ${formatDate(it.options.deliveryDate, lang)}`
            );
        if (it.options.properties?.length) {
            it.options.properties.forEach(([k, v]) => lines.push(`${k}: ${v}`));
        }
        return lines;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={closeCart}
                        className="fixed inset-0 z-[60] bg-stone-900/40"
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="fixed top-0 right-0 z-[61] h-full w-full max-w-md bg-white shadow-xl flex flex-col"
                        data-testid="cart-drawer"
                    >
                        <div className="flex items-center justify-between px-5 h-14 border-b border-stone-200">
                            <h2 className="text-lg font-bold text-ink" data-testid="cart-title">
                                {t("cart.title")}
                                {totalQty > 0 && ` (${totalQty})`}
                            </h2>
                            <button
                                onClick={closeCart}
                                data-testid="cart-drawer-close"
                                aria-label={t("nav.close")}
                                className="p-2 text-ink hover:text-bordeaux transition-colors"
                            >
                                <X className="h-5 w-5" strokeWidth={1.8} />
                            </button>
                        </div>

                        {items.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
                                <Heart className="h-8 w-8 text-bordeaux/30" strokeWidth={1.5} />
                                <p className="text-lg font-bold text-ink">{t("cart.empty")}</p>
                                <p className="text-sm text-stone-500">{t("cart.emptySub")}</p>
                                <Link
                                    to="/"
                                    onClick={closeCart}
                                    data-testid="cart-empty-cta"
                                    className="mt-2 px-8 py-3 bg-bordeaux text-white text-sm font-semibold rounded-lg hover:bg-bordeaux-deep transition-colors"
                                >
                                    {t("cart.emptyCta")}
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                                    {items.map((it) => (
                                        <div
                                            key={it.lineId}
                                            className="flex gap-3 border-b border-stone-100 pb-4 last:border-0"
                                        >
                                            <img
                                                src={it.image}
                                                alt={it.name}
                                                className="h-24 w-20 object-cover rounded-lg shrink-0 bg-stone-100"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-sm font-semibold text-ink leading-snug">
                                                        {it.name}
                                                    </p>
                                                    <button
                                                        onClick={() => removeItem(it.lineId)}
                                                        data-testid="cart-item-remove-button"
                                                        aria-label={t("cart.remove")}
                                                        className="p-1 text-stone-400 hover:text-bordeaux transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                    </button>
                                                </div>
                                                <p className="text-sm font-bold text-ink mt-0.5">
                                                    {formatPrice(it.unitPrice, lang)}
                                                </p>
                                                <div className="mt-1 space-y-0.5">
                                                    {optionsSummary(it).map((line, i) => (
                                                        <p key={i} className="text-[11px] text-stone-500 truncate">
                                                            {line}
                                                        </p>
                                                    ))}
                                                </div>
                                                <div className="mt-2 flex items-center border border-stone-200 rounded-lg w-fit">
                                                    <button
                                                        onClick={() => updateQty(it.lineId, -1)}
                                                        data-testid="cart-qty-minus"
                                                        aria-label={t("cart.qtyMinus")}
                                                        className="px-3 py-1.5 text-ink hover:text-bordeaux transition-colors"
                                                    >
                                                        <Minus className="h-3 w-3" />
                                                    </button>
                                                    <span
                                                        data-testid="cart-item-qty"
                                                        className="px-3 text-sm font-semibold"
                                                    >
                                                        {it.qty}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQty(it.lineId, 1)}
                                                        data-testid="cart-qty-plus"
                                                        aria-label={t("cart.qtyPlus")}
                                                        className="px-3 py-1.5 text-ink hover:text-bordeaux transition-colors"
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="border-t border-stone-200 px-5 py-4 space-y-1.5">
                                    <div className="flex justify-between text-sm text-stone-600">
                                        <span>{t("cart.subtotal")}</span>
                                        <span data-testid="cart-subtotal-price">
                                            {formatPrice(subtotal, lang)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm text-stone-600">
                                        <span>{t("cart.shipping")}</span>
                                        <span data-testid="cart-delivery-fee">
                                            {freeDelivery ? (
                                                <span
                                                    data-testid="cart-delivery-free"
                                                    className="font-bold text-bordeaux"
                                                >
                                                    <s className="font-normal text-stone-400 mr-1.5">
                                                        {formatPrice(DELIVERY_FEE, lang)}
                                                    </s>
                                                    {t("cart.free")}
                                                </span>
                                            ) : (
                                                formatPrice(deliveryFee, lang)
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-baseline pt-2 border-t border-stone-200">
                                        <span className="text-base font-bold text-ink">
                                            {t("cart.total")}
                                        </span>
                                        <span
                                            data-testid="cart-total-price"
                                            className="text-xl font-bold text-ink"
                                        >
                                            {formatPrice(total, lang)}
                                        </span>
                                    </div>
                                    <button
                                        onClick={checkoutShopify}
                                        disabled={checkingOut || items.length === 0}
                                        data-testid="checkout-demo-button"
                                        className="mt-2 w-full py-3.5 bg-bordeaux text-white text-sm font-bold rounded-lg hover:bg-bordeaux-deep transition-colors disabled:opacity-60"
                                    >
                                        {checkingOut ? t("cart.redirecting") : t("cart.checkout")}
                                    </button>
                                    <p className="text-center text-[10px] text-stone-400 leading-relaxed">
                                        {t("cart.secureNote")}
                                    </p>
                                </div>
                            </>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};
